// Floating ticker that surfaces every OPEN Deriv contract for the active
// account. Rendered at the page root in Dashboard so it persists across
// tab switches (Trade / Bot / Signals / History).
//
// Behaviour:
//   • Hidden when there are no open contracts.
//   • Single contract  → compact pill (one row).
//   • Multiple         → primary contract shown + "+N more" badge that
//                        toggles a vertical stack on click.
//   • Each row shows symbol · direction · live P&L $ · P&L % · Sell.
//   • Sticky at the bottom (above mobile bottom-nav and the
//     "Made with Emergent" platform badge).
import { useState } from "react";
import { TrendingUp, TrendingDown, X, ChevronUp, ChevronDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useActiveContracts } from "@/hooks/useActiveContracts";

const labelFor = (ct) => {
  const u = (ct || "").toUpperCase();
  if (u === "CALL") return "Rise";
  if (u === "PUT") return "Fall";
  if (u === "MULTUP") return "Mult ↑";
  if (u === "MULTDOWN") return "Mult ↓";
  if (u === "DIGITDIFF") return "Differ";
  if (u === "DIGITEVEN") return "Even";
  if (u === "DIGITODD") return "Odd";
  if (u === "ONETOUCH") return "Touch";
  if (u === "NOTOUCH") return "No-Touch";
  return u || "—";
};

const isLong = (ct) => {
  const u = (ct || "").toUpperCase();
  return u === "CALL" || u === "MULTUP" || u === "ONETOUCH" || u === "DIGITEVEN";
};

const ContractRow = ({ c, onSell, selling }) => {
  const up = isLong(c.contract_type);
  const profit = Number(c.profit) || 0;
  const profitPct = Number(c.profit_pct) || 0;
  const good = profit >= 0;
  // Show "—" while Deriv hasn't streamed a bid_price yet (pending=true) so
  // we don't display a misleading −100% loss on a brand-new winning trade.
  const pending = c.pending;
  return (
    <div
      data-testid={`active-trade-row-${c.contract_id}`}
      className="flex items-center gap-3 px-3 py-2.5 hover:bg-white/5 transition"
    >
      <span
        className={`flex items-center justify-center h-7 w-7 rounded-md ${
          up ? "bg-vts-profit/15 text-vts-profit" : "bg-vts-loss/15 text-vts-loss"
        }`}
      >
        {up ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-mono text-white truncate">
            {c.symbol || c.display_name || "—"}
          </span>
          <span className="text-[10px] font-mono uppercase tracking-wider text-vts-silver/70">
            {labelFor(c.contract_type)}
            {c.multiplier ? ` ×${c.multiplier}` : ""}
          </span>
        </div>
        <div className="text-[10px] font-mono text-vts-silver/50 mt-0.5">
          stake ${c.buy_price.toFixed(2)}
          {pending ? " · syncing…" : ` · live $${c.bid_price.toFixed(2)}`}
        </div>
      </div>
      <div className={`text-right tabular-nums leading-tight ${
        pending ? "text-vts-silver/50" : good ? "text-vts-profit" : "text-vts-loss"
      }`}>
        <div className="text-sm font-mono font-semibold">
          {pending ? "—" : `${good ? "+" : ""}${profit.toFixed(2)}`}
        </div>
        <div className="text-[10px] font-mono opacity-80">
          {pending ? "pending" : `${good ? "+" : ""}${profitPct.toFixed(1)}%`}
        </div>
      </div>
      <button
        data-testid={`active-trade-sell-${c.contract_id}`}
        onClick={() => onSell(c.contract_id)}
        disabled={selling}
        className="ml-1 text-[11px] font-mono uppercase tracking-wider px-2.5 py-1 rounded border border-vts-line text-vts-silver/80 hover:bg-vts-loss/15 hover:text-vts-loss hover:border-vts-loss/40 transition disabled:opacity-40"
      >
        {selling ? <Loader2 className="h-3 w-3 animate-spin" /> : "Sell"}
      </button>
    </div>
  );
};

const ActiveTradeRibbon = ({ authorized }) => {
  const { contracts, sellContract } = useActiveContracts(authorized);
  const [expanded, setExpanded] = useState(false);
  const [sellingId, setSellingId] = useState(null);
  const [hidden, setHidden] = useState(false);

  if (!authorized || contracts.length === 0 || hidden) return null;

  const handleSell = async (id) => {
    setSellingId(id);
    const r = await sellContract(id);
    setSellingId(null);
    if (r.ok) {
      toast.success(`Sold for $${r.sold_for.toFixed(2)}`, { description: `Contract ${id}` });
    } else {
      toast.error(r.error || "Sell failed");
    }
  };

  const primary = contracts[0];
  // Exclude pending rows from the header total — pending means we don't yet
  // know the real P&L, so including a `0` profit was misleading.
  const totalPnl = contracts
    .filter((c) => !c.pending)
    .reduce((a, c) => a + (Number(c.profit) || 0), 0);
  const totalGood = totalPnl >= 0;

  return (
    <div
      data-testid="active-trade-ribbon"
      className="fixed left-2 right-2 sm:left-4 sm:right-4 lg:left-auto lg:right-6 lg:w-[420px] z-40 bottom-24 lg:bottom-6"
      style={{ pointerEvents: "auto" }}
    >
      <div className="rounded-lg overflow-hidden border border-vts-blue/40 bg-vts-bg/95 backdrop-blur-md shadow-[0_8px_30px_rgba(0,40,140,0.45)]">
        {/* Header strip — always visible */}
        <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-vts-blue/20 via-vts-blue/10 to-transparent border-b border-vts-blue/30">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-vts-blue opacity-70"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-vts-blue"></span>
          </span>
          <div className="text-[11px] font-mono uppercase tracking-wider text-vts-silver/90 flex-1 truncate">
            Live trades · {contracts.length} open
          </div>
          <div className={`text-xs font-mono font-semibold tabular-nums ${totalGood ? "text-vts-profit" : "text-vts-loss"}`}>
            {totalGood ? "+" : ""}{totalPnl.toFixed(2)} USD
          </div>
          {contracts.length > 1 && (
            <button
              data-testid="active-trade-toggle"
              onClick={() => setExpanded((v) => !v)}
              className="text-vts-silver/70 hover:text-white p-1"
              aria-label={expanded ? "Collapse" : "Expand"}
            >
              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </button>
          )}
          <button
            data-testid="active-trade-dismiss"
            onClick={() => setHidden(true)}
            className="text-vts-silver/50 hover:text-vts-loss p-1"
            aria-label="Hide ribbon (re-appears on next open contract)"
            title="Hide (will re-appear on the next trade)"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Primary contract — always visible */}
        <div className="divide-y divide-vts-line/60">
          <ContractRow
            c={primary}
            onSell={handleSell}
            selling={sellingId === primary.contract_id}
          />
          {expanded &&
            contracts.slice(1).map((c) => (
              <ContractRow
                key={c.contract_id}
                c={c}
                onSell={handleSell}
                selling={sellingId === c.contract_id}
              />
            ))}
        </div>
      </div>
    </div>
  );
};

export default ActiveTradeRibbon;
